import { yupResolver } from '@hookform/resolvers/yup'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import MuiAlert, { AlertProps } from '@mui/material/Alert'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useTranslation } from 'react-i18next'
import { loginSchema } from '~/src/schemas'
import { TLoginFormValues } from '~/src/types/form'
import { inputFieldStyle, textFieldStyle } from './LoginFormFeature.styles'

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
})

type Props = {
  onSubmit: OnSubmitHandler
  isAuthenticatedError: boolean
  loading: boolean
}

export type OnSubmitHandler = (values: TLoginFormValues) => Promise<void>

export const LoginFormFeature: React.FC<Props> = ({ onSubmit, isAuthenticatedError, loading }) => {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TLoginFormValues>({
    resolver: yupResolver(loginSchema),
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoginError, setIsLoginError] = useState(false)

  useEffect(() => {
    setIsLoginError(isAuthenticatedError)
  }, [isAuthenticatedError])

  /**
   * handleClickShowPassword
   */
  const handleClickShowPassword = () => setShowPassword(!showPassword)

  /**
   * handleMouseDownPassword
   * @param event {React.MouseEvent<HTMLButtonElement>}
   */
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  /**
   * handleClose
   * @param event
   * @param reason
   */
  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    setIsLoginError(false)
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <TextField
            label={t('login.user_id')}
            variant="outlined"
            {...register('username', { required: true })}
            error={'username' in errors}
            sx={textFieldStyle}
          />
          {errors.username?.type === 'required' && (
            <Typography variant="caption" color="error">
              {t('login.required')}
            </Typography>
          )}
        </FormControl>

        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="outlined-password" sx={{ fontSize: '1rem' }}>
              {t('login.password')}
            </InputLabel>
            <OutlinedInput
              id="outlined-password"
              type={showPassword ? 'text' : 'password'}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label={t('login.password')}
              {...register('password', { required: true, min: 6 })}
              error={'password' in errors}
              sx={inputFieldStyle}
            />
          </FormControl>
          {errors.password?.type === 'required' && (
            <Typography variant="caption" color="error">
              {t('login.required')}
            </Typography>
          )}
        </Box>
        <LoadingButton
          size="large"
          fullWidth
          type="submit"
          variant="contained"
          loading={loading}
          loadingPosition="start"
          startIcon={<></>}
          sx={{ background: '#1F4266', fontSize: '15px' }}
        >
          {t('login.login')}
        </LoadingButton>
      </form>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={isLoginError}
        autoHideDuration={4000}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity="error" sx={{ width: '100%' }}>
          ログインに失敗しました
        </Alert>
      </Snackbar>
    </>
  )
}
